/**
 * Turns an export's own SVG into a PNG, in the browser — the client-side half of board OCR.
 * Gemini needs a raster image; rasterising server-side would mean adding an image library,
 * precisely the dependency the SVG export format was chosen to avoid in the first place
 * (plan/identity-and-portal.md: "no headless browser, no raster library, no new
 * dependency"). The portal already displays this exact SVG in a browser
 * (`PortalExports.ts`'s own `<img>` thumbnail and viewer), which can draw it to a
 * `<canvas>` and read a PNG back out with no dependency at all.
 *
 * This only works because the download route is same-origin: an `<img>` loaded
 * cross-origin without a CORS-permitting response taints the canvas it is drawn onto, and a
 * tainted canvas throws on `toBlob`/`toDataURL` rather than producing anything. A same-origin
 * load never taints the canvas regardless of any `crossorigin` attribute or CORS headers —
 * confirmed directly against a real Chromium engine (a throwaway same-origin `<img>` →
 * `<canvas>` → `toDataURL('image/png')`, not assumed from the spec alone), not merely against
 * this app's own route, which was not yet reachable in isolation the way that check needed.
 * If the export route ever moves cross-origin, this breaks loudly — `toBlob` rejects — rather
 * than silently, so there is nothing here to guard against that case specially.
 */
export async function rasterizeExportToPng(downloadUrl: string): Promise<Blob> {
  const image = await loadImage(downloadUrl);

  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width || 1;
  canvas.height = image.naturalHeight || image.height || 1;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get a 2D canvas context to rasterise the export.');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Rasterising the export produced no image data.'));
      }
    }, 'image/png');
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load the export to rasterise it.'));
    image.src = src;
  });
}
